import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Blog settings state (mock - would be stored in Firestore in real app)
  const [blogSettings, setBlogSettings] = useState({
    defaultMetaTitleSuffix: ' | Enteeon',
    defaultMetaDescription: 'Sustainable biotech solutions for a better future.',
    siteName: 'Enteeon'
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      setSuccess('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      let errorMessage = 'Failed to update password';
      if (err.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBlogSettingsChange = (e) => {
    const { name, value } = e.target;
    setBlogSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveBlogSettings = () => {
    // In a real app, this would save to Firestore
    setSuccess('Blog settings saved successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  return (
    <div>
      <h2 className="mb-4">Settings</h2>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
        </div>
      )}

      <div className="row">
        {/* Admin Profile Section */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-0">
              <h5 className="mb-0">Admin Profile</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={user?.email || ''}
                  readOnly
                />
                <small className="text-muted">Email cannot be changed</small>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Account Created</label>
                <input
                  type="text"
                  className="form-control"
                  value={user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}
                  readOnly
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Last Sign In</label>
                <input
                  type="text"
                  className="form-control"
                  value={user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'N/A'}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-0">
              <h5 className="mb-0">Change Password</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange}>
                <div className="mb-3">
                  <label htmlFor="currentPassword" className="form-label fw-semibold">
                    Current Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label fw-semibold">
                    New Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    minLength="6"
                  />
                  <small className="text-muted">Must be at least 6 characters long</small>
                </div>
                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label fw-semibold">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Settings Section */}
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">Blog Settings</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="siteName" className="form-label fw-semibold">
                Site Name
              </label>
              <input
                type="text"
                className="form-control"
                id="siteName"
                name="siteName"
                value={blogSettings.siteName}
                onChange={handleBlogSettingsChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="defaultMetaTitleSuffix" className="form-label fw-semibold">
                Default Meta Title Suffix
              </label>
              <input
                type="text"
                className="form-control"
                id="defaultMetaTitleSuffix"
                name="defaultMetaTitleSuffix"
                value={blogSettings.defaultMetaTitleSuffix}
                onChange={handleBlogSettingsChange}
                placeholder=" | Your Site Name"
              />
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="defaultMetaDescription" className="form-label fw-semibold">
              Default Meta Description
            </label>
            <textarea
              className="form-control"
              id="defaultMetaDescription"
              name="defaultMetaDescription"
              rows="3"
              value={blogSettings.defaultMetaDescription}
              onChange={handleBlogSettingsChange}
              placeholder="Default description for blog posts"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveBlogSettings}
          >
            Save Blog Settings
          </button>
        </div>
      </div>

      {/* Firebase Info Section */}
      <div className="card shadow-sm border-0 rounded-3 mt-4">
        <div className="card-header bg-white border-0">
          <h5 className="mb-0">System Information</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">Firebase Project ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={process.env.REACT_APP_FIREBASE_PROJECT_ID || 'Not configured'}
                  readOnly
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-semibold">Environment</label>
                <input
                  type="text"
                  className="form-control"
                  value={process.env.NODE_ENV || 'development'}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Info Section */}
      <div className="alert alert-info mt-4">
        <h6 className="mb-2"><i className="bi bi-graph-up me-2"></i>Analytics Status</h6>
        <p className="mb-0 small">
          Analytics data is automatically generated and updated daily at 2 AM UTC. Check the Analytics Dashboard to view real-time metrics.
        </p>
      </div>
    </div>
  );
};

export default AdminSettings;
